import React from "react";
import {
  Stack,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Button,
} from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import moment from "moment";
import {
  getMonthRange,
  monthKeyFromParts,
  shiftMonthKey,
} from "../../utils/monthDateRange";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Compact month/year navigator for history lists.
 * Value is a "YYYY-MM" key.
 */
function MonthYearPicker({
  value,
  onChange,
  minYear,
  maxYear,
  showToday = true,
  size = "small",
  disabled = false,
}) {
  const current = React.useMemo(() => getMonthRange(new Date()), []);
  const resolvedMaxYear = maxYear ?? current.year;
  const resolvedMinYear = minYear ?? current.year - 10;

  const selected = React.useMemo(() => {
    const key = value || current.key;
    const m = moment(key, "YYYY-MM");
    return {
      key,
      year: m.year(),
      month: m.month(),
    };
  }, [value, current.key]);

  const years = React.useMemo(() => {
    const list = [];
    for (let y = resolvedMaxYear; y >= resolvedMinYear; y -= 1) {
      list.push(y);
    }
    return list;
  }, [resolvedMaxYear, resolvedMinYear]);

  const canGoNext = selected.key < current.key;

  const emit = React.useCallback(
    (key) => {
      if (typeof onChange === "function") onChange(key);
    },
    [onChange],
  );

  const handleMonthChange = (event) => {
    emit(monthKeyFromParts(selected.year, event.target.value));
  };

  const handleYearChange = (event) => {
    emit(monthKeyFromParts(event.target.value, selected.month));
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      flexWrap="wrap"
      useFlexGap
    >
      <IconButton
        size={size}
        onClick={() => emit(shiftMonthKey(selected.key, -1))}
        disabled={disabled}
        aria-label="Previous month"
      >
        <NavigateBeforeIcon fontSize="small" />
      </IconButton>

      <FormControl size={size} sx={{ minWidth: 120 }} disabled={disabled}>
        <Select
          value={selected.month}
          onChange={handleMonthChange}
          variant="outlined"
          sx={{ "& .MuiSelect-select": { py: 0.75 } }}
          aria-label="Month"
        >
          {MONTH_NAMES.map((name, index) => (
            <MenuItem key={name} value={index}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size={size} sx={{ minWidth: 88 }} disabled={disabled}>
        <Select
          value={selected.year}
          onChange={handleYearChange}
          variant="outlined"
          sx={{ "& .MuiSelect-select": { py: 0.75 } }}
          aria-label="Year"
        >
          {years.map((y) => (
            <MenuItem key={y} value={y}>
              {y}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <IconButton
        size={size}
        onClick={() => emit(shiftMonthKey(selected.key, 1))}
        disabled={disabled || !canGoNext}
        aria-label="Next month"
      >
        <NavigateNextIcon fontSize="small" />
      </IconButton>

      {showToday && selected.key !== current.key && (
        <Button
          size={size}
          variant="outlined"
          onClick={() => emit(current.key)}
          disabled={disabled}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 20 }}
        >
          This month
        </Button>
      )}
    </Stack>
  );
}

export default MonthYearPicker;
